/**
 * Blob Service - Vercel Blob and Custom Backend Integration
 *
 * Provides file storage using:
 * 1. Vercel Blob Storage (production, auto-configured)
 * 2. Custom Content Server (files.freeweb3.com)
 * 3. localStorage fallback (development)
 *
 * NO Infura dependencies - uses modern blob storage
 */

import { uploadToBlob } from './uploadClient';

class BlobService {
  constructor() {
    this.contentServerUrl = (process.env.REACT_APP_CONTENT_SERVER_URL || 'https://files.freeweb3.com').trim();
    this.contentServerApiUrl = (process.env.REACT_APP_CONTENT_SERVER_API_URL || 'https://files.freeweb3.com/api').trim();
    this.isLocalDev = this.detectLocalDevelopment();

    // Use Vercel Blob as primary (version fixed to 0.20.0)
    // Falls back to custom backend if Vercel Blob fails
    // Set REACT_APP_USE_BLOB_STORAGE=false to use custom backend as primary
    this.useBlobStorage = process.env.REACT_APP_USE_BLOB_STORAGE !== 'false';

    console.log('[BlobService] Initialized', {
      mode: this.isLocalDev ? 'LOCAL_DEV' : 'PRODUCTION',
      contentServer: this.contentServerUrl,
      useBlobStorage: this.useBlobStorage,
      usingVercelBlob: this.useBlobStorage
    });
  }

  /**
   * Detect if running in local development
   * @private
   */
  detectLocalDevelopment() {
    return (
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );
  }

  /**
   * Upload file to blob storage
   *
   * Flow:
   * - Production: Upload to Vercel Blob or custom backend
   * - Development: Store in localStorage
   *
   * @param {File} file - File to upload
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Upload result with URL and hash
   */
  async uploadFile(file, metadata = {}) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📤 [BlobService] UPLOAD FILE START');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📄 File Details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified).toISOString()
    });
    console.log('📋 Metadata:', metadata);
    console.log('🔧 Service Config:', {
      isLocalDev: this.isLocalDev,
      useBlobStorage: this.useBlobStorage,
      contentServerUrl: this.contentServerUrl,
      contentServerApiUrl: this.contentServerApiUrl
    });

    try {
      // Generate SHA-256 hash
      console.log('🔐 [BlobService] Generating SHA-256 hash...');
      const startHash = Date.now();
      const fileHash = await this.generateFileHash(file);
      console.log(`✅ [BlobService] Hash generated in ${Date.now() - startHash}ms:`, fileHash);

      // Development mode: use localStorage
      if (this.isLocalDev) {
        console.warn('⚠️ [BlobService] DEVELOPMENT MODE: Using localStorage fallback');
        return await this.uploadToLocalStorage(file, fileHash, metadata);
      }

      // Production mode: Multi-tier fallback system
      console.log('🎯 [BlobService] Starting multi-tier upload with fallback');

      // Try Vercel Blob first (most reliable after version fix)
      if (this.useBlobStorage) {
        console.log('🔵 [BlobService] PRIMARY: Trying VERCEL BLOB storage...');
        try {
          const result = await this.uploadToVercelBlob(file, fileHash, metadata);
          console.log('✅ [BlobService] Vercel Blob upload succeeded!');
          return result;
        } catch (blobError) {
          console.warn('⚠️ [BlobService] Vercel Blob failed, trying custom backend...', blobError.message);
          // Fall through to custom backend
        }
      }

      // Try custom backend
      console.log('🟢 [BlobService] FALLBACK: Trying CUSTOM BACKEND storage...');
      try {
        const result = await this.uploadToCustomBackend(file, fileHash, metadata);
        console.log('✅ [BlobService] Custom backend upload succeeded!');
        return result;
      } catch (customError) {
        console.error('❌ [BlobService] Custom backend also failed:', customError.message);

        // If custom backend was primary and failed, try Vercel Blob as last resort
        if (!this.useBlobStorage) {
          console.log('🔵 [BlobService] LAST RESORT: Trying Vercel Blob...');
          try {
            const result = await this.uploadToVercelBlob(file, fileHash, metadata);
            console.log('✅ [BlobService] Vercel Blob upload succeeded as fallback!');
            return result;
          } catch (lastError) {
            console.error('❌ [BlobService] All storage options exhausted');
            throw lastError;
          }
        }

        throw customError;
      }
    } catch (error) {
      console.error('❌ [BlobService] Upload failed:', error);
      console.error('❌ [BlobService] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw new Error(`File upload failed: ${error.message}`);
    }
  }

  /**
   * Generate SHA-256 hash of file
   * @private
   */
  async generateFileHash(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Upload to localStorage (development only)
   * @private
   */
  async uploadToLocalStorage(file, fileHash, metadata) {
    try {
      const fileData = await this.fileToBase64(file);

      const storageObject = {
        hash: fileHash,
        name: file.name,
        size: file.size,
        type: file.type,
        data: fileData,
        metadata: metadata,
        uploadedAt: new Date().toISOString()
      };

      localStorage.setItem(`file_${fileHash}`, JSON.stringify(storageObject));

      const localUrl = `http://localhost:3000/blob/${fileHash}`;

      console.log('✓ File stored in localStorage:', fileHash);

      return {
        success: true,
        hash: fileHash,
        url: localUrl,
        blobUrl: localUrl,
        fileName: file.name,
        fileSize: file.size,
        storage: 'localStorage'
      };
    } catch (error) {
      console.error('[BlobService] localStorage upload failed:', error);
      throw error;
    }
  }

  /**
   * Convert file to base64
   * @private
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload to Vercel Blob Storage directly from the browser.
   * An authenticated token request goes to /api/get-upload-url; the file bytes
   * never pass through a serverless function (no 4.5 MB body limit).
   * @private
   */
  async uploadToVercelBlob(file, fileHash, metadata) {
    console.log('[VercelBlob] Starting direct client upload', { size: file.size });

    try {
      const blob = await uploadToBlob(file, fileHash, { onProgress: metadata?.onProgress });

      return {
        success: true,
        hash: fileHash,
        url: blob.url,
        blobUrl: blob.url,
        fileName: file.name,
        fileSize: file.size,
        storage: 'vercel-blob'
      };
    } catch (error) {
      console.error('[VercelBlob] Upload failed:', error.message);
      throw error;
    }
  }

  /**
   * Upload to custom content server (files.freeweb3.com)
   * @private
   */
  async uploadToCustomBackend(file, fileHash, metadata) {
    console.log('┌────────────────────────────────────────┐');
    console.log('│  CUSTOM BACKEND UPLOAD                 │');
    console.log('└────────────────────────────────────────┘');

    try {
      console.log('📦 [CustomBackend] Preparing FormData...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('hash', fileHash);
      formData.append('metadata', JSON.stringify(metadata));
      console.log('✅ [CustomBackend] FormData prepared');

      const uploadUrl = `${this.contentServerApiUrl}/upload`;
      console.log('🌐 [CustomBackend] Upload URL:', uploadUrl);
      console.log('📤 [CustomBackend] Starting fetch POST request...');

      const startUpload = Date.now();
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      const duration = Date.now() - startUpload;
      console.log(`📨 [CustomBackend] Response received in ${duration}ms`);
      console.log('📊 [CustomBackend] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Could not read error response');
        console.error('❌ [CustomBackend] Upload failed with status:', response.status);
        console.error('❌ [CustomBackend] Error response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      console.log('✅ [CustomBackend] Response OK, parsing JSON...');
      const result = await response.json();
      console.log('📋 [CustomBackend] Response data:', result);

      const blobUrl = `${this.contentServerUrl}/blob/${fileHash}`;
      console.log('🔗 [CustomBackend] Generated blob URL:', blobUrl);

      console.log('✅✅✅ [CustomBackend] UPLOAD SUCCESSFUL! ✅✅✅');

      return {
        success: true,
        hash: fileHash,
        url: blobUrl,
        blobUrl: blobUrl,
        fileName: file.name,
        fileSize: file.size,
        storage: 'custom-backend'
      };
    } catch (error) {
      console.error('❌❌❌ [CustomBackend] UPLOAD FAILED! ❌❌❌');
      console.error('❌ [CustomBackend] Error:', error);
      console.error('❌ [CustomBackend] Error message:', error.message);
      console.error('❌ [CustomBackend] Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Retrieve file from blob storage
   *
   * @param {string} hash - File hash or URL
   * @returns {Promise<Blob>} File blob
   */
  async retrieveFile(hash) {
    try {
      // Try localStorage first (development)
      if (this.isLocalDev) {
        const stored = localStorage.getItem(`file_${hash}`);
        if (stored) {
          const fileData = JSON.parse(stored);
          const base64 = fileData.data.split(',')[1];
          const binary = atob(base64);
          const array = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i);
          }
          return new Blob([array], { type: fileData.type });
        }
      }

      // Try custom content server
      const url = `${this.contentServerUrl}/blob/${hash}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`File not found: ${hash}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('[BlobService] Retrieve file failed:', error);
      throw error;
    }
  }

  /**
   * Get file URL
   *
   * @param {string} hash - File hash
   * @returns {string} File URL
   */
  getFileUrl(hash) {
    if (this.isLocalDev) {
      return `http://localhost:3000/blob/${hash}`;
    }
    return `${this.contentServerUrl}/blob/${hash}`;
  }

  /**
   * Delete file from blob storage
   *
   * @param {string} hash - File hash
   * @returns {Promise<boolean>} Success status
   */
  async deleteFile(hash) {
    try {
      if (this.isLocalDev) {
        localStorage.removeItem(`file_${hash}`);
        return true;
      }

      const response = await fetch(`${this.contentServerApiUrl}/delete/${hash}`, {
        method: 'DELETE'
      });

      return response.ok;
    } catch (error) {
      console.error('[BlobService] Delete file failed:', error);
      return false;
    }
  }
}

// Export singleton instance
const blobService = new BlobService();

export default blobService;
export { BlobService };
